// src/pages/modules/Admin/PhotoImporter/PhotoImporter.tsx
// รองรับ 2 formats:
//   A (nested): { [customerId]: { [photoId]: { image, ... } } }
//   B (flat):   [ { userid, id, image, ... }, ... ]  ← จาก split_photos.py
// เลือกได้หลายไฟล์พร้อมกัน

import React from 'react'
import { Badge, Button, Card, Col, ProgressBar, Row, Spinner } from 'react-bootstrap'
import { dbSet, dbRead } from '../../../../_services/firebase'
import { DB_PHOTO_ITEM, DB_PHOTO_BY_ID } from '../../../../_config/db_paths'
import { toastSuccess } from '../../../../_utils'

// ─── Types ───────────────────────────────────────────────────
interface FlatPhoto {
  userid: string
  id: string
  image?: string
  name?: string
  size?: number
  sort?: number
  type?: string
  createdAt?: number
}

interface FileInfo {
  name: string
  count: number
  status: 'waiting' | 'parsing' | 'ready' | 'error'
  error?: string
  photos: FlatPhoto[]
}

interface ImportResult {
  customerId: string
  done: number
  skipped: number
  status: 'waiting' | 'running' | 'done' | 'error'
  error?: string
}

// ─── Helpers ─────────────────────────────────────────────────
function ts() {
  return `[${new Date().toLocaleTimeString('th-TH')}]`
}

// Normalize any format → FlatPhoto[]
function normalize(parsed: any, fileName: string): FlatPhoto[] {
  // Format B: array
  if (Array.isArray(parsed)) {
    return parsed.filter((p) => p.userid && p.id)
  }
  // Format A: nested object
  if (typeof parsed === 'object' && parsed !== null) {
    const flat: FlatPhoto[] = []
    for (const [customerId, photos] of Object.entries(parsed)) {
      if (typeof photos !== 'object' || !photos) continue
      for (const [photoId, photo] of Object.entries(photos as Record<string, any>)) {
        if (typeof photo !== 'object' || !photo) continue
        flat.push({ userid: customerId, id: photo.id || photoId, ...photo })
      }
    }
    return flat
  }
  return []
}

// ─── PhotoImporter ────────────────────────────────────────────
export default function PhotoImporter() {
  const [files, setFiles] = React.useState<FileInfo[]>([])
  const [overwrite, setOverwrite] = React.useState(false)
  const [running, setRunning] = React.useState(false)
  const [results, setResults] = React.useState<ImportResult[]>([])
  const [logLines, setLogLines] = React.useState<string[]>([])
  const [done, setDone] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const addLog = (msg: string) =>
    setLogLines((prev) => [`${ts()} ${msg}`, ...prev])

  const totalPhotos = files.filter((f) => f.status === 'ready').reduce((s, f) => s + f.count, 0)
  const readyFiles = files.filter((f) => f.status === 'ready')
  const doneCount = results.filter((r) => r.status === 'done').length
  const errorCount = results.filter((r) => r.status === 'error').length
  const overallPct = results.length > 0 ? Math.round((doneCount / results.length) * 100) : 0

  // ─── Handle file selection ────────────────────────────────
  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || [])
    if (!selected.length) return
    e.target.value = ''

    // init as parsing
    const newFiles: FileInfo[] = selected.map((f) => ({
      name: f.name, count: 0, status: 'parsing', photos: [],
    }))
    setFiles((prev) => [...prev, ...newFiles])
    setDone(false)

    // parse each file
    for (let i = 0; i < selected.length; i++) {
      const file = selected[i]
      const fileIndex = files.length + i

      try {
        const buf = await file.arrayBuffer()
        const text = new TextDecoder('utf-8').decode(buf)
        const trimmed = text.trim()

        if (!trimmed.endsWith('}') && !trimmed.endsWith(']')) {
          setFiles((prev) => prev.map((f, idx) =>
            idx === fileIndex
              ? { ...f, status: 'error', error: `ไฟล์ไม่สมบูรณ์ (truncated) — ท้ายสุด: "${trimmed.slice(-30)}"` }
              : f
          ))
          addLog(`✗ ${file.name} — ไฟล์ไม่สมบูรณ์`)
          continue
        }

        const parsed = JSON.parse(text)
        const photos = normalize(parsed, file.name)

        if (photos.length === 0) {
          setFiles((prev) => prev.map((f, idx) =>
            idx === fileIndex
              ? { ...f, status: 'error', error: 'ไม่พบรูปภาพหรือ format ไม่ถูกต้อง' }
              : f
          ))
          addLog(`✗ ${file.name} — ไม่พบรูปภาพ`)
          continue
        }

        setFiles((prev) => prev.map((f, idx) =>
          idx === fileIndex
            ? { ...f, status: 'ready', count: photos.length, photos }
            : f
        ))
        addLog(`✅ ${file.name} — ${photos.length} รูป`)

      } catch (err: any) {
        setFiles((prev) => prev.map((f, idx) =>
          idx === fileIndex
            ? { ...f, status: 'error', error: err?.message || 'parse error' }
            : f
        ))
        addLog(`✗ ${file.name} — ${err?.message}`)
      }
    }
  }

  const removeFile = (idx: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx))

  const clearAll = () => {
    setFiles([])
    setResults([])
    setLogLines([])
    setDone(false)
  }

  // ─── Start import ─────────────────────────────────────────
  const startImport = async () => {
    if (!readyFiles.length || running) return

    // group all photos by customerId
    const grouped: Record<string, FlatPhoto[]> = {}
    for (const f of readyFiles) {
      for (const p of f.photos) {
        if (!grouped[p.userid]) grouped[p.userid] = []
        grouped[p.userid].push(p)
      }
    }

    const customerIds = Object.keys(grouped)
    setRunning(true)
    setDone(false)
    setResults(customerIds.map((cid) => ({
      customerId: cid, done: 0, skipped: 0, status: 'waiting',
    })))
    addLog(`🚀 เริ่มนำเข้า ${customerIds.length} ลูกค้า, ${totalPhotos} รูป (overwrite=${overwrite})`)

    let totalImported = 0
    let totalSkipped = 0

    for (let ci = 0; ci < customerIds.length; ci++) {
      const customerId = customerIds[ci]
      const photos = grouped[customerId]

      setResults((prev) => prev.map((r, i) =>
        i === ci ? { ...r, status: 'running' } : r
      ))

      try {
        // load existing if not overwrite
        let existing: Record<string, any> = {}
        if (!overwrite) {
          const raw = await dbRead<Record<string, any>>(DB_PHOTO_BY_ID(customerId))
          existing = raw || {}
        }

        let donePics = 0
        let skipPics = 0

        // ใหม่ — setResults อยู่นอก loop
        for (const p of photos) {
          if (!overwrite && existing[p.id]) {
            skipPics++
            totalSkipped++
            continue
          }
          if (!p.image) { skipPics++; continue }

          await dbSet(DB_PHOTO_ITEM(customerId, p.id), {
            id: p.id,
            image: p.image,
            name: p.name || 'photo.jpg',
            size: p.size || 0,
            type: p.type || 'image/jpeg',
            sort: p.sort || 0,
            createdAt: p.createdAt || Date.now(),
          })
          donePics++
          totalImported++
        }

        // อัพเดต state ครั้งเดียวหลัง loop จบ
        setResults((prev) => prev.map((r, i) =>
          i === ci ? { ...r, done: donePics, skipped: skipPics } : r
        ))
        addLog(`✓ ${customerId.slice(0, 36)}... — ${donePics} รูป${skipPics > 0 ? `, ข้าม ${skipPics}` : ''}`)

      } catch (err: any) {
        setResults((prev) => prev.map((r, i) =>
          i === ci ? { ...r, status: 'error', error: err?.message } : r
        ))
        addLog(`✗ ${customerId.slice(0, 36)}... — Error: ${err?.message}`)
      }
    }

    setRunning(false)
    setDone(true)
    addLog(`🏁 เสร็จสิ้น — นำเข้า ${totalImported} รูป, ข้าม ${totalSkipped} รูป${errorCount > 0 ? `, error ${errorCount} ลูกค้า` : ''}`)
    toastSuccess(`นำเข้าเสร็จสิ้น — ${totalImported} รูป`)
  }

  // ─── Render ───────────────────────────────────────────────
  return (
    <div className='p-3'>
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>นำเข้ารูปภาพจาก JSON</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>
            Super Admin เท่านั้น — รองรับ flat array (จาก split_photos.py) และ nested format
          </div>
        </div>
        {files.length > 0 && !running && (
          <Button size='sm' variant='outline-danger' onClick={clearAll}>
            <i className='fa-solid fa-trash me-1' />ล้างทั้งหมด
          </Button>
        )}
      </div>

      {/* ─── Step 1: เลือกไฟล์ ───────────────────────────── */}
      <Card className='shadow-sm mb-3'>
        <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
          1. เลือกไฟล์ JSON
          <span className='text-muted ms-2' style={{ fontSize: 11, fontWeight: 400 }}>
            เลือกได้หลายไฟล์พร้อมกัน
          </span>
        </Card.Header>
        <Card.Body>
          <input
            ref={inputRef}
            type='file'
            accept='.json,application/json'
            multiple
            style={{ display: 'none' }}
            onChange={handleFiles}
            disabled={running}
          />
          <Button
            variant='outline-primary' size='sm'
            disabled={running}
            onClick={() => inputRef.current?.click()}
          >
            <i className='fa-solid fa-file-import me-1' />
            เลือกไฟล์ JSON (หลายไฟล์ได้)
          </Button>

          {/* File list */}
          {files.length > 0 && (
            <div className='mt-3 d-flex flex-column gap-2'>
              {files.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 10px', borderRadius: 8,
                  border: `1px solid ${f.status === 'ready' ? '#c8e6c9' : f.status === 'error' ? '#ffcdd2' : '#e0e0e0'}`,
                  background: f.status === 'ready' ? '#f1f8e9' : f.status === 'error' ? '#fff5f5' : '#fafafa',
                  fontSize: 12,
                }}>
                  {/* icon */}
                  <span style={{ fontSize: 16 }}>
                    {f.status === 'parsing' && <Spinner size='sm' animation='border' style={{ width: 14, height: 14 }} />}
                    {f.status === 'ready' && <i className='fa-solid fa-circle-check' style={{ color: '#2e7d32' }} />}
                    {f.status === 'error' && <i className='fa-solid fa-circle-xmark' style={{ color: '#c62828' }} />}
                  </span>
                  {/* name */}
                  <span style={{ flex: 1, fontWeight: 600 }}>{f.name}</span>
                  {/* count */}
                  {f.status === 'ready' && (
                    <Badge bg='success' style={{ fontSize: 10 }}>{f.count} รูป</Badge>
                  )}
                  {f.status === 'error' && (
                    <span style={{ color: '#c62828', fontSize: 11 }}>{f.error}</span>
                  )}
                  {/* remove */}
                  {!running && (
                    <button onClick={() => removeFile(i)} style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#aaa', fontSize: 13, padding: '0 2px',
                    }}>✕</button>
                  )}
                </div>
              ))}

              {/* Summary */}
              {readyFiles.length > 0 && (
                <div className='mt-1' style={{ fontSize: 12, color: '#555' }}>
                  <i className='fa-solid fa-circle-info me-1 text-primary' />
                  พร้อมนำเข้า <strong>{readyFiles.length} ไฟล์</strong>, รวม <strong>{totalPhotos} รูป</strong>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ─── Step 2: ตั้งค่า + เริ่ม ──────────────────────── */}
      {readyFiles.length > 0 && (
        <Card className='shadow-sm mb-3'>
          <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
            2. ตั้งค่าและเริ่มนำเข้า
          </Card.Header>
          <Card.Body>
            <Row className='g-3 align-items-center'>
              <Col xs={12} md={6}>
                <label style={{ fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type='checkbox' checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} disabled={running} />
                  <span>
                    Overwrite — เขียนทับรูปที่มีอยู่แล้ว
                    <span className='text-muted ms-1' style={{ fontSize: 11 }}>(ถ้าไม่ติ๊ก จะข้าม photoId ที่มีอยู่)</span>
                  </span>
                </label>
              </Col>
              <Col xs={12} md={6}>
                <Button variant='warning' disabled={running} onClick={startImport} className='w-100'>
                  {running
                    ? <><Spinner size='sm' animation='border' className='me-1' />กำลังนำเข้า...</>
                    : <><i className='fa-solid fa-cloud-arrow-up me-1' />เริ่มนำเข้า {totalPhotos} รูป ({readyFiles.length} ไฟล์)</>
                  }
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ─── Step 3: Progress ─────────────────────────────── */}
      {results.length > 0 && (
        <Card className='shadow-sm mb-3'>
          <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
            3. ความคืบหน้า
            {done && (
              <Badge bg={errorCount > 0 ? 'warning' : 'success'} className='ms-2' style={{ fontSize: 11 }}>
                {errorCount > 0 ? `เสร็จ (${errorCount} error)` : '✓ เสร็จสมบูรณ์'}
              </Badge>
            )}
          </Card.Header>
          <Card.Body>
            <div className='d-flex justify-content-between mb-1' style={{ fontSize: 12 }}>
              <span>ภาพรวม</span>
              <span>{doneCount + errorCount} / {results.length} ลูกค้า ({overallPct}%)</span>
            </div>
            <ProgressBar className='mb-3'>
              <ProgressBar variant='success' now={Math.round((doneCount / results.length) * 100)} key={1} />
              <ProgressBar variant='danger' now={Math.round((errorCount / results.length) * 100)} key={2} />
            </ProgressBar>

            {/* Log-style results list — newest first */}
            <div style={{
              maxHeight: 300, overflowY: 'auto',
              background: '#1e1e1e', borderRadius: 8,
              padding: '8px 12px',
              display: 'flex', flexDirection: 'column',
            }}>
              {/* currently running item pinned at top */}
              {running && results.find((r) => r.status === 'running') && (() => {
                const r = results.find((r) => r.status === 'running')!
                return (
                  <div style={{ color: '#ffd54f', fontFamily: 'monospace', fontSize: 11, paddingBottom: 4, borderBottom: '1px solid #333', marginBottom: 4 }}>
                    <Spinner size='sm' animation='border' style={{ width: 10, height: 10, marginRight: 6 }} />
                    {r.customerId.slice(0, 40)}... ({r.done} รูป)
                  </div>
                )
              })()}

              {/* done/error items newest first */}
              {[...results].filter((r) => r.status === 'done' || r.status === 'error').reverse().map((r) => (
                <div key={r.customerId} style={{
                  fontFamily: 'monospace', fontSize: 11,
                  color: r.status === 'done' ? '#81c784' : '#ef9a9a',
                  lineHeight: 1.7,
                }}>
                  {r.status === 'done' ? '✓' : '✗'}{' '}
                  {r.customerId.slice(0, 36)}...{' '}
                  {r.status === 'done'
                    ? `${r.done} รูป${r.skipped > 0 ? `, ข้าม ${r.skipped}` : ''}`
                    : `Error: ${r.error}`
                  }
                </div>
              ))}

              {/* waiting items at bottom */}
              {results.filter((r) => r.status === 'waiting').length > 0 && (
                <div style={{ color: '#555', fontFamily: 'monospace', fontSize: 11, marginTop: 4 }}>
                  รออยู่อีก {results.filter((r) => r.status === 'waiting').length} ลูกค้า...
                </div>
              )}
            </div>

            {/* Final summary */}
            {done && (
              <div className='mt-3 d-flex gap-3 flex-wrap' style={{ fontSize: 13 }}>
                <span><strong style={{ color: '#2e7d32' }}>{results.reduce((s, r) => s + r.done, 0)}</strong> รูปนำเข้าสำเร็จ</span>
                <span><strong style={{ color: '#888' }}>{results.reduce((s, r) => s + r.skipped, 0)}</strong> รูปถูกข้าม</span>
                {errorCount > 0 && <span><strong style={{ color: '#c62828' }}>{errorCount}</strong> ลูกค้าเกิด error</span>}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ─── Log panel ────────────────────────────────────── */}
      {logLines.length > 0 && (
        <Card className='shadow-sm'>
          <Card.Header style={{ fontSize: 13, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><i className='fa-solid fa-terminal me-2' />Log</span>
            <Button size='sm' variant='outline-secondary' style={{ fontSize: 11 }} onClick={() => setLogLines([])}>ล้าง</Button>
          </Card.Header>
          <Card.Body style={{ padding: 0 }}>
            <div style={{ maxHeight: 200, overflowY: 'auto', background: '#1e1e1e', borderRadius: '0 0 8px 8px', padding: '8px 12px' }}>
              {logLines.map((line, i) => {
                const color =
                  line.includes('✅') || line.includes('✓') || line.includes('🏁') ? '#81c784' :
                    line.includes('✗') || line.includes('Error') ? '#ef9a9a' :
                      line.includes('🚀') ? '#ffd54f' : '#bbb'
                return (
                  <div key={i} style={{ fontFamily: 'monospace', fontSize: 11, color, lineHeight: 1.7 }}>
                    {line}
                  </div>
                )
              })}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}